import functionParser from '@ali/vu-function-parser';

const PATTERN = {
  EMAIL: /^([a-zA-Z0-9_.-]+)@([\da-zA-Z.-]+)\.([a-zA-Z.]{2,6})$/,
  URL: /^https?:\/\/(?!-)(?:[a-zA-Z\d-]{0,62}[a-zA-Z\d]\.){1,126}(?!\d+)[a-zA-Z\d]{1,63}/,
  HEX: /^#?([a-fA-F0-9]{6}|[a-fA-F0-9]{3})$/i,
  NUM: /^((-?\d+\.\d+)|(-?\d+)|(-?\.\d+))$/,
  IDCARD: /(^\d{15}$)|(^\d{17}([0-9]|X)$)/i,
  CNMOBILE: /^(0|86|17951)?(13[0-9]|14[5-9]|15[0-9]|16[124567]|17[0-8]|18[0-9]|19[0-9]|92[0-9]|98[0-9])[0-9]{8}$/,
  IPV4: /^(((\d{1,2})|(1\d{2})|(2[0-4]\d)|(25[0-5]))\.){3}((\d{1,2})|(1\d{2})|(2[0-4]\d)|(25[0-5]))$/,
  IPV6: /^\s*((([0-9A-Fa-f]{1,4}:){7}([0-9A-Fa-f]{1,4}|:))|(([0-9A-Fa-f]{1,4}:){6}(:[0-9A-Fa-f]{1,4}|((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3})|:))|(([0-9A-Fa-f]{1,4}:){5}(((:[0-9A-Fa-f]{1,4}){1,2})|:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3})|:))|(([0-9A-Fa-f]{1,4}:){4}(((:[0-9A-Fa-f]{1,4}){1,3})|((:[0-9A-Fa-f]{1,4})?:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:))|(([0-9A-Fa-f]{1,4}:){3}(((:[0-9A-Fa-f]{1,4}){1,4})|((:[0-9A-Fa-f]{1,4}){0,2}:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:))|(([0-9A-Fa-f]{1,4}:){2}(((:[0-9A-Fa-f]{1,4}){1,5})|((:[0-9A-Fa-f]{1,4}){0,3}:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:))|(([0-9A-Fa-f]{1,4}:){1}(((:[0-9A-Fa-f]{1,4}){1,6})|((:[0-9A-Fa-f]{1,4}){0,4}:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:))|(:(((:[0-9A-Fa-f]{1,4}){1,7})|((:[0-9A-Fa-f]{1,4}){0,5}:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:)))(%.+)?\s*$/,
  MAC: /^([0-9a-fA-F]{2})(([/\s:-][0-9a-fA-F]{2}){5})$/,
};

const builtins = {
  required: {
    valid: (value) => {
      if (value === false || value === 0) {
        return true;
      }
      return !!value && value.length !== 0 && value !== false;
    },
    defaultMessage: {
      en_US: '%s cannot be empty',
      zh_CN: '%s不能为空',
    },
  },

  number: {
    valid: value => PATTERN.NUM.test(value),
    defaultMessage: {
      en_US: '%s shoule be number',
      zh_CN: '%s应当为数字',
    },
  },

  integer: {
    // FIXME: builtins.number -> builtins.number.valid()
    valid: value => builtins.number(value) && parseInt(value, 10) === value - 0,
    defaultMessage: {
      en_US: '%s should be integer',
      zh_CN: '%s应当为整数',
    },
  },

  email: {
    valid: value => typeof value === 'string' && PATTERN.EMAIL.test(value),
    defaultMessage: {
      en_US: '%s format error',
      zh_CN: '%s格式错误',
    },
  },

  mobile: {
    valid: value => typeof value === 'string' && PATTERN.CNMOBILE.test(value),
    defaultMessage: {
      en_US: '%s format error',
      zh_CN: '%s格式错误',
    },
  },

  idCard: {
    valid: value => typeof value === 'string' && PATTERN.IDCARD.test(value),
    defaultMessage: {
      en_US: '%s format error',
      zh_CN: '%s格式错误',
    },
  },

  url: {
    valid: value => typeof value === 'string' && PATTERN.URL.test(value),
    defaultMessage: {
      en_US: '%s format error',
      zh_CN: '%s格式错误',
    },
  },

  ip: {
    valid: (value, param) => {
      if (typeof value !== 'string') {
        return false;
      }
      const { types } = param;
      if (types.indexOf('ipv4') >= 0 && PATTERN.IPV4.test(value)) {
        return true;
      }
      if (types.indexOf('ipv6') >= 0 && PATTERN.IPV6.test(value)) {
        return true;
      }
      return false;
    },
    defaultMessage: {
      en_US: '%s should be an ip address',
      zh_CN: '%s应当为 IP 地址',
    },
  },

  mac: {
    valid: value => typeof value === 'string' && PATTERN.MAC.test(value),
    defaultMessage: {
      en_US: '%s should be a mac address',
      zh_CN: '%s应当为 MAC 地址',
    },
  },

  minLength: {
    valid: (value, param) => value.length >= Number(param),
    defaultMessage: {
      en_US: '%s minimum length is %s',
      zh_CN: '%s最小长度为 %s',
    },
  },

  maxLength: {
    valid: (value, param) => value.length <= Number(param),
    defaultMessage: {
      en_US: '%s maximum length is %s',
      zh_CN: '%s最大长度为 %s',
    },
  },

  minValue: {
    valid: (value, param) => Number(value) >= Number(param),
    defaultMessage: {
      en_US: '%s minimum value is %s',
      zh_CN: '%s最小值为 %s',
    },
  },

  maxValue: {
    valid: (value, param) => Number(value) <= Number(param),
    defaultMessage: {
      en_US: '%s maximum value is %s',
      zh_CN: '%s最大值为 %s',
    },
  },

  regular: {
    valid: (value, param) => {
      const m = param.match(/.*\/([gimy]*)$/);
      const flags = m && m[1] ? m[1] : '';
      const pattern = param.replace(new RegExp(`^/(.*?)/${flags}$`), '$1');
      const regexp = new RegExp(pattern, flags);
      return regexp.test(value);
    },
    defaultMessage: {
      en_US: '%s format error',
      zh_CN: '%s格式错误',
    },
  },

  customValidate: {
    valid: (value, param, state, ctx) => {
      const func = functionParser(param);
      if (typeof func === 'function') {
        const res = func.call(ctx.getActionContext(state), value, state, ctx);
        return res !== false;
      }
      return true;
    },
    defaultMessage: {
      en_US: '%s verification failed',
      zh_CN: '%s验证失败',
    },
  },

  defaultValueFormatter(state) {
    const fieldData = state.getValue('fieldData');
    if (fieldData) {
      if (typeof fieldData.value === 'number') {
        return fieldData.value.toString();
      }
      return fieldData.value;
    }
    return '';
  },
  validationKeys: ['fieldData', 'value', 'defaultValue'],
};

// 兼容 customRegexp
builtins.customRegexp = builtins.regular;

export default builtins;
